import { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Download, Image, FileText } from 'lucide-react';

const DISCLAIMER = "None of this is financial advice. For informational purposes only. No guarantees, only probabilities based on TA.";
const WEBSITE_URL = "savageaisignals.com";

// Check if mobile device
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Check if Web Share API is available for files
const canShareFiles = () => {
  return navigator.share && navigator.canShare;
};

// Convert canvas to blob
const canvasToBlob = (canvas) => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png', 1.0);
  });
};

// Share/Save image using native share sheet (for mobile - saves to Photos)
const shareImage = async (canvas, filename) => {
  try {
    const blob = await canvasToBlob(canvas);
    const file = new File([blob], filename, { type: 'image/png' });
    
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: 'Savage AI Signal',
        text: 'Trading signal from Savage AI Signals'
      });
      return true;
    }
    return false;
  } catch (error) {
    console.log('Share failed or cancelled:', error);
    return false;
  }
};

// Fallback download method
const downloadImage = (canvas, filename) => {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  link.click();
};

// Format time horizon for display
const formatTimeHorizon = (horizon) => {
  const labels = {
    'scalp': '⚡ Scalp',
    'short_term': '📊 Short Term',
    'mid_term': '📈 Mid Term',
    'long_term': '🎯 Long Term'
  };
  return labels[horizon] || horizon;
};

const formatPrice = (price) => {
  if (!price || isNaN(price)) return '$0.00';
  const num = parseFloat(price);
  
  if (Math.abs(num) >= 1000) {
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (Math.abs(num) >= 1) {
    return `$${num.toFixed(4)}`;
  }
  if (Math.abs(num) >= 0.01) {
    return `$${num.toFixed(4)}`;
  }
  if (Math.abs(num) < 0.01) {
    const str = num.toExponential();
    const match = str.match(/e-(\d+)/);
    if (match) {
      const exponent = parseInt(match[1]);
      const decimals = Math.min(exponent + 4, 12);
      return `$${num.toFixed(decimals)}`;
    }
    return `$${num.toFixed(8)}`;
  }
  return `$${num.toFixed(2)}`;
};

// Individual Signal Card for Download
export function SignalDownloadCard({ signal, showDownloadButton = true }) {
  const cardRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const generateImage = async () => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: '#0f172a',
      scale: 2,
      useCORS: true,
      logging: false
    });
    return canvas;
  };

  const downloadAsImage = async () => {
    setDownloading(true);
    try {
      const canvas = await generateImage();
      if (canvas) {
        const assetName = signal.asset_name || signal.ticker || 'signal';
        const filename = `savage-ai-signal-${assetName}-${Date.now()}.png`;
        
        // On mobile, try to use share sheet (saves to Photos)
        if (isMobile() && canShareFiles()) {
          const shared = await shareImage(canvas, filename);
          if (!shared) {
            // Fallback to regular download
            downloadImage(canvas, filename);
          }
        } else {
          downloadImage(canvas, filename);
        }
      }
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setDownloading(false);
      setShowMenu(false);
    }
  };

  const downloadAsPDF = async () => {
    setDownloading(true);
    try {
      const canvas = await generateImage();
      if (canvas) {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'px',
          format: [canvas.width / 2, canvas.height / 2]
        });
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
        const assetName = signal.asset_name || signal.ticker || 'signal';
        pdf.save(`savage-ai-signal-${assetName}-${Date.now()}.pdf`);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setDownloading(false);
      setShowMenu(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Download Button */}
      {showDownloadButton && (
        <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', zIndex: 10 }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            disabled={downloading}
            style={{
              background: 'rgba(102, 126, 234, 0.9)',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.6rem 0.8rem',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              fontSize: '0.95rem'
            }}
          >
            <Download size={18} />
            {downloading ? 'Saving...' : ''}
          </button>
          
          {showMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '0.35rem',
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              minWidth: '180px'
            }}>
              <button
                onClick={downloadAsImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.85rem 1rem',
                  background: 'transparent',
                  border: 'none',
                  color: '#f1f5f9',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => e.target.style.background = '#334155'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <Image size={18} /> Save as PNG
              </button>
              <button
                onClick={downloadAsPDF}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.85rem 1rem',
                  background: 'transparent',
                  border: 'none',
                  color: '#f1f5f9',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => e.target.style.background = '#334155'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <FileText size={18} /> Save as PDF
              </button>
            </div>
          )}
        </div>
      )}

      {/* Downloadable Card Content */}
      <div 
        ref={cardRef}
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          padding: '1.75rem',
          borderRadius: '1rem',
          border: '1px solid #334155',
          minWidth: '360px',
          maxWidth: '420px'
        }}
      >
        {/* Header with Branding */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1.25rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #334155'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>📈</span>
            <span style={{ fontWeight: 'bold', fontSize: '1.15rem', color: '#10b981' }}>Savage AI Signals</span>
          </div>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{WEBSITE_URL}</span>
        </div>

        {/* Signal Info */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#f1f5f9' }}>
              {signal.asset_name || signal.ticker || 'Trade Signal'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
              <span style={{
                background: signal.signal === 'LONG' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                color: signal.signal === 'LONG' ? '#10b981' : '#ef4444',
                padding: '0.35rem 0.85rem',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                fontWeight: '700'
              }}>
                {signal.signal}
              </span>
              {signal.time_horizon && (
                <span style={{ 
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {formatTimeHorizon(signal.time_horizon)}
                </span>
              )}
              {signal.accuracy_score !== undefined && signal.accuracy_score !== null && (
                <span style={{ 
                  background: signal.accuracy_score >= 80 ? '#10b981' : signal.accuracy_score >= 70 ? '#f59e0b' : '#6b7280',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  🎯 {Number(signal.accuracy_score).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          {signal.asset_type && (
            <span style={{ fontSize: '0.95rem', color: '#94a3b8' }}>{signal.asset_type}</span>
          )}
        </div>

        {/* Price Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.85rem',
          marginBottom: '1.25rem'
        }}>
          <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '0.5rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.35rem' }}>Current Price</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#f1f5f9' }}>{formatPrice(signal.current_price)}</div>
          </div>
          {signal.ideal_entry && (
            <div style={{ background: 'rgba(167, 139, 250, 0.1)', padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.9rem', color: '#a78bfa', marginBottom: '0.35rem' }}>Ideal Entry</div>
              <div style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#a78bfa' }}>{formatPrice(signal.ideal_entry)}</div>
            </div>
          )}
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '1rem', borderRadius: '0.5rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#ef4444', marginBottom: '0.35rem' }}>Stop Loss</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#ef4444' }}>{formatPrice(signal.stop_loss)}</div>
          </div>
          <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '1rem', borderRadius: '0.5rem' }}>
            <div style={{ fontSize: '0.9rem', color: '#10b981', marginBottom: '0.35rem' }}>Take Profit</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#10b981' }}>{formatPrice(signal.take_profit)}</div>
          </div>
        </div>

        {/* Analysis */}
        {signal.analysis && (
          <div style={{ 
            background: 'rgba(255,255,255,0.03)', 
            padding: '1rem', 
            borderRadius: '0.5rem',
            marginBottom: '1.25rem',
            fontSize: '1rem',
            color: '#94a3b8',
            lineHeight: '1.6'
          }}>
            {signal.analysis}
          </div>
        )}

        {/* Timestamp */}
        <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1rem' }}>
          Generated: {new Date(signal.shared_at || Date.now()).toLocaleString()}
        </div>

        {/* Disclaimer */}
        <div style={{
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '0.5rem',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#fbbf24',
          lineHeight: '1.5'
        }}>
          ⚠️ {DISCLAIMER}
        </div>
      </div>
    </div>
  );
}

// Batch Download Component - Downloads up to 20 signals in ONE image
export function BatchSignalDownload({ signals }) {
  const [downloading, setDownloading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const batchCardRef = useRef(null);

  const downloadBatchAsImage = async () => {
    setDownloading(true);
    try {
      if (batchCardRef.current) {
        const canvas = await html2canvas(batchCardRef.current, {
          backgroundColor: '#0f172a',
          scale: 2,
          useCORS: true,
          logging: false
        });
        const filename = `savage-ai-signals-${Date.now()}.png`;
        
        // On mobile, try to use share sheet (saves to Photos)
        if (isMobile() && canShareFiles()) {
          const shared = await shareImage(canvas, filename);
          if (!shared) {
            downloadImage(canvas, filename);
          }
        } else {
          downloadImage(canvas, filename);
        }
      }
    } catch (error) {
      console.error('Error batch downloading:', error);
    } finally {
      setDownloading(false);
      setShowMenu(false);
    }
  };

  const downloadBatchAsPDF = async () => {
    setDownloading(true);
    try {
      if (batchCardRef.current) {
        const canvas = await html2canvas(batchCardRef.current, {
          backgroundColor: '#0f172a',
          scale: 2,
          useCORS: true,
          logging: false
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [canvas.width / 2, canvas.height / 2]
        });
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
        pdf.save(`savage-ai-signals-${Date.now()}.pdf`);
      }
    } catch (error) {
      console.error('Error batch downloading PDF:', error);
    } finally {
      setDownloading(false);
      setShowMenu(false);
    }
  };

  if (!signals || signals.length === 0) return null;

  const signalsToDownload = signals.slice(0, 35);

  return (
    <div>
      {/* Header with Batch Download Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>🎯 Individual Signals</h2>
          <p style={{ fontSize: '1rem', color: '#94a3b8' }}>
            {signals.length} signals available
          </p>
        </div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            disabled={downloading}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.75rem 1.25rem',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            <Download size={20} />
            {downloading ? 'Downloading...' : `Download Latest ${Math.min(signals.length, 35)}`}
          </button>
          
          {showMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '0.35rem',
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              minWidth: '200px',
              zIndex: 20
            }}>
              <button
                onClick={downloadBatchAsImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '1rem',
                  background: 'transparent',
                  border: 'none',
                  color: '#f1f5f9',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => e.target.style.background = '#334155'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <Image size={18} /> Save as PNG
              </button>
              <button
                onClick={downloadBatchAsPDF}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '1rem',
                  background: 'transparent',
                  border: 'none',
                  color: '#f1f5f9',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => e.target.style.background = '#334155'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <FileText size={18} /> Save as PDF
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hidden card for batch download - 35 signals in ONE image */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div 
          ref={batchCardRef}
          style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            padding: '2rem',
            borderRadius: '1rem',
            border: '1px solid #334155',
            width: '1400px'
          }}
        >
          {/* Header with Branding */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #334155'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '2rem' }}>🎯</span>
              <div>
                <span style={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#10b981', display: 'block' }}>Savage AI Signals</span>
                <span style={{ fontSize: '1rem', color: '#64748b' }}>Individual Trading Signals</span>
              </div>
            </div>
            <span style={{ fontSize: '1.1rem', color: '#64748b' }}>{WEBSITE_URL}</span>
          </div>

          {/* Signals count and time */}
          <div style={{ marginBottom: '1.25rem', fontSize: '1.1rem', color: '#94a3b8' }}>
            {signalsToDownload.length} signals • {new Date().toLocaleString()}
          </div>

          {/* Signals Grid - 5 columns for 35 signals */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '0.85rem',
            marginBottom: '1.5rem'
          }}>
            {signalsToDownload.map((signal, index) => (
              <div key={index} style={{
                background: 'rgba(255,255,255,0.03)',
                padding: '1rem',
                borderRadius: '0.75rem',
                border: '1px solid #334155'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '1.15rem', color: '#f1f5f9' }}>
                    {signal.asset_name || signal.ticker || 'Signal'}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    {signal.accuracy_score !== undefined && signal.accuracy_score !== null && (
                      <span style={{ 
                        background: signal.accuracy_score >= 80 ? '#10b981' : signal.accuracy_score >= 70 ? '#f59e0b' : '#6b7280',
                        padding: '0.15rem 0.4rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: 'white'
                      }}>
                        {Number(signal.accuracy_score).toFixed(0)}%
                      </span>
                    )}
                    <span style={{
                      background: signal.signal === 'LONG' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      color: signal.signal === 'LONG' ? '#10b981' : '#ef4444',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {signal.signal}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Price</div>
                    <div style={{ color: '#f1f5f9', fontWeight: '600' }}>{formatPrice(signal.current_price)}</div>
                  </div>
                  {signal.ideal_entry && (
                    <div>
                      <div style={{ color: '#a78bfa', fontSize: '0.75rem' }}>Entry</div>
                      <div style={{ color: '#a78bfa', fontWeight: '600' }}>{formatPrice(signal.ideal_entry)}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ color: '#ef4444', fontSize: '0.75rem' }}>Stop Loss</div>
                    <div style={{ color: '#ef4444', fontWeight: '600' }}>{formatPrice(signal.stop_loss)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#10b981', fontSize: '0.75rem' }}>Take Profit</div>
                    <div style={{ color: '#10b981', fontWeight: '600' }}>{formatPrice(signal.take_profit)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div style={{
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.85rem 1.25rem',
            fontSize: '0.9rem',
            color: '#fbbf24',
            lineHeight: '1.5'
          }}>
            ⚠️ {DISCLAIMER}
          </div>
        </div>
      </div>

      {/* Visible cards with individual download buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {signals.map((signal, index) => (
          <SignalDownloadCard key={index} signal={signal} />
        ))}
      </div>
    </div>
  );
}

// Market Scan Card - PAGINATED: Splits signals into multiple high-quality pages
export function MarketScanDownloadCard({ marketScan, title = "📊 Latest Market Scan" }) {
  const pageRefs = useRef([]);
  const [showMenu, setShowMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');

  const signals = marketScan?.market_scan_data || [];
  const scanTitle = title;
  
  // Split signals into pages of 12 each (4 rows x 3 columns)
  const SIGNALS_PER_PAGE = 12;
  const pages = [];
  for (let i = 0; i < signals.length; i += SIGNALS_PER_PAGE) {
    pages.push(signals.slice(i, i + SIGNALS_PER_PAGE));
  }

  const generatePageImage = async (pageIndex) => {
    if (!pageRefs.current[pageIndex]) return null;
    const canvas = await html2canvas(pageRefs.current[pageIndex], {
      backgroundColor: '#0f172a',
      scale: 3, // Higher scale for better quality
      useCORS: true,
      logging: false
    });
    return canvas;
  };

  const downloadAsImages = async () => {
    setDownloading(true);
    try {
      for (let i = 0; i < pages.length; i++) {
        setDownloadProgress(`Saving page ${i + 1} of ${pages.length}...`);
        const canvas = await generatePageImage(i);
        if (canvas) {
          const filename = `savage-ai-market-scan-page${i + 1}-${Date.now()}.png`;
          
          if (isMobile() && canShareFiles()) {
            const shared = await shareImage(canvas, filename);
            if (!shared) {
              downloadImage(canvas, filename);
            }
          } else {
            downloadImage(canvas, filename);
          }
          // Small delay between downloads
          if (i < pages.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
    } catch (error) {
      console.error('Error generating images:', error);
    } finally {
      setDownloading(false);
      setDownloadProgress('');
      setShowMenu(false);
    }
  };

  const downloadAsPDF = async () => {
    setDownloading(true);
    try {
      setDownloadProgress('Generating PDF...');
      
      // Generate first page to get dimensions
      const firstCanvas = await generatePageImage(0);
      if (!firstCanvas) return;
      
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [firstCanvas.width / 3, firstCanvas.height / 3]
      });
      
      // Add first page
      const firstImgData = firstCanvas.toDataURL('image/png');
      pdf.addImage(firstImgData, 'PNG', 0, 0, firstCanvas.width / 3, firstCanvas.height / 3);
      
      // Add remaining pages
      for (let i = 1; i < pages.length; i++) {
        setDownloadProgress(`Processing page ${i + 1} of ${pages.length}...`);
        const canvas = await generatePageImage(i);
        if (canvas) {
          pdf.addPage([canvas.width / 3, canvas.height / 3], 'landscape');
          const imgData = canvas.toDataURL('image/png');
          pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 3, canvas.height / 3);
        }
      }
      
      pdf.save(`savage-ai-market-scan-${Date.now()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setDownloading(false);
      setDownloadProgress('');
      setShowMenu(false);
    }
  };

  return (
    <div className="card" style={{ position: 'relative', marginBottom: '1.5rem' }}>
      {/* Header with Download Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{scanTitle}</h2>
          <p style={{ fontSize: '1rem', color: '#94a3b8' }}>
            {signals.length} active signals • {pages.length} page{pages.length > 1 ? 's' : ''} • Shared {new Date(marketScan?.shared_at).toLocaleString()}
          </p>
        </div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            disabled={downloading}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.75rem 1.25rem',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            <Download size={20} />
            {downloading ? downloadProgress || 'Saving...' : `Download (${pages.length} ${pages.length === 1 ? 'page' : 'pages'})`}
          </button>
          
          {showMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '0.35rem',
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              minWidth: '200px',
              zIndex: 20
            }}>
              <button
                onClick={downloadAsImages}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '1rem',
                  background: 'transparent',
                  border: 'none',
                  color: '#f1f5f9',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => e.target.style.background = '#334155'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <Image size={18} /> Save as PNG ({pages.length} files)
              </button>
              <button
                onClick={downloadAsPDF}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '1rem',
                  background: 'transparent',
                  border: 'none',
                  color: '#f1f5f9',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => e.target.style.background = '#334155'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <FileText size={18} /> Save as PDF (1 file)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Signal List (visible to users) */}
      <div style={{ maxHeight: '500px', overflowY: 'auto', marginBottom: '1rem' }}>
        {signals.map((signal, index) => (
          <div key={index} className="signal-card" style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{signal.ticker}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {signal.accuracy_score !== undefined && signal.accuracy_score !== null && (
                  <span style={{ 
                    background: signal.accuracy_score >= 80 ? '#10b981' : signal.accuracy_score >= 70 ? '#f59e0b' : '#6b7280',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: 'white'
                  }}>
                    🎯 {Number(signal.accuracy_score).toFixed(0)}%
                  </span>
                )}
                <span style={{
                  background: signal.signal === 'LONG' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                  color: signal.signal === 'LONG' ? '#10b981' : '#ef4444',
                  padding: '0.3rem 0.75rem',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}>
                  {signal.signal}
                </span>
              </div>
            </div>
            <div className="signal-price-grid" style={{ display: 'grid', gap: '0.75rem', fontSize: '1rem' }}>
              <div>
                <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Price</div>
                <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{formatPrice(signal.current_price)}</div>
              </div>
              <div>
                <div style={{ color: '#06b6d4', fontSize: '0.9rem' }}>Entry</div>
                <div style={{ fontWeight: '600', color: '#06b6d4', fontSize: '1.1rem' }}>{formatPrice(signal.ideal_entry || signal.current_price * (signal.signal === 'LONG' ? 0.995 : 1.005))}</div>
              </div>
              <div>
                <div style={{ color: '#ef4444', fontSize: '0.9rem' }}>Stop Loss</div>
                <div style={{ fontWeight: '600', color: '#ef4444', fontSize: '1.1rem' }}>{formatPrice(signal.stop_loss)}</div>
              </div>
              <div>
                <div style={{ color: '#10b981', fontSize: '0.9rem' }}>Take Profit</div>
                <div style={{ fontWeight: '600', color: '#10b981', fontSize: '1.1rem' }}>{formatPrice(signal.take_profit)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Hidden pages for download - Each page has 12 signals (4 rows x 3 columns) */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        {pages.map((pageSignals, pageIndex) => (
          <div 
            key={pageIndex}
            ref={el => pageRefs.current[pageIndex] = el}
            style={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              padding: '2rem',
              borderRadius: '1rem',
              border: '1px solid #334155',
              width: '1400px',
              marginBottom: '20px'
            }}
          >
            {/* Header with Branding */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #334155'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '2.5rem' }}>📊</span>
                <div>
                  <span style={{ fontWeight: 'bold', fontSize: '1.75rem', color: '#10b981', display: 'block' }}>Savage AI Signals</span>
                  <span style={{ fontSize: '1.1rem', color: '#64748b' }}>{scanTitle.replace('📊 ', '').replace('🔷 ', '').replace('💎 ', '')}</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '1.2rem', color: '#64748b', display: 'block' }}>{WEBSITE_URL}</span>
                <span style={{ fontSize: '1rem', color: '#94a3b8' }}>Page {pageIndex + 1} of {pages.length}</span>
              </div>
            </div>

            {/* Scan Info */}
            <div style={{ marginBottom: '1.25rem', fontSize: '1.2rem', color: '#94a3b8' }}>
              {signals.length} total signals • Showing {pageIndex * SIGNALS_PER_PAGE + 1}-{Math.min((pageIndex + 1) * SIGNALS_PER_PAGE, signals.length)} • {new Date(marketScan?.shared_at || Date.now()).toLocaleString()}
            </div>

            {/* Signals Grid - 3 columns, 4 rows max per page */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.25rem',
              marginBottom: '1.5rem'
            }}>
              {pageSignals.map((signal, index) => (
                <div key={index} style={{
                  background: 'rgba(255,255,255,0.05)',
                  padding: '1.5rem',
                  borderRadius: '0.75rem',
                  border: '1px solid #334155'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#f1f5f9' }}>{signal.ticker}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {signal.accuracy_score !== undefined && signal.accuracy_score !== null && (
                        <span style={{ 
                          background: signal.accuracy_score >= 80 ? '#10b981' : signal.accuracy_score >= 70 ? '#f59e0b' : '#6b7280',
                          padding: '0.3rem 0.6rem',
                          borderRadius: '0.25rem',
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: 'white'
                        }}>
                          🎯 {Number(signal.accuracy_score).toFixed(0)}%
                        </span>
                      )}
                      <span style={{
                        background: signal.signal === 'LONG' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                        color: signal.signal === 'LONG' ? '#10b981' : '#ef4444',
                        padding: '0.3rem 0.75rem',
                        borderRadius: '0.25rem',
                        fontSize: '1.1rem',
                        fontWeight: '700'
                      }}>
                        {signal.signal}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '1.1rem' }}>
                    <div>
                      <div style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '0.25rem' }}>Price</div>
                      <div style={{ color: '#f1f5f9', fontWeight: '600', fontSize: '1.2rem' }}>{formatPrice(signal.current_price)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#a78bfa', fontSize: '0.95rem', marginBottom: '0.25rem' }}>Entry</div>
                      <div style={{ color: '#a78bfa', fontWeight: '600', fontSize: '1.2rem' }}>{formatPrice(signal.ideal_entry || signal.current_price * (signal.signal === 'LONG' ? 0.995 : 1.005))}</div>
                    </div>
                    <div>
                      <div style={{ color: '#ef4444', fontSize: '0.95rem', marginBottom: '0.25rem' }}>Stop Loss</div>
                      <div style={{ color: '#ef4444', fontWeight: '600', fontSize: '1.2rem' }}>{formatPrice(signal.stop_loss)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#10b981', fontSize: '0.95rem', marginBottom: '0.25rem' }}>Take Profit</div>
                      <div style={{ color: '#10b981', fontWeight: '600', fontSize: '1.2rem' }}>{formatPrice(signal.take_profit)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '0.5rem',
              padding: '1rem 1.5rem',
              fontSize: '1rem',
              color: '#fbbf24',
              lineHeight: '1.5'
            }}>
              ⚠️ {DISCLAIMER}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DownloadableCardExports = { SignalDownloadCard, MarketScanDownloadCard, BatchSignalDownload };
export default DownloadableCardExports;
